import { useCallback, useMemo } from 'react';
import { useDropzone, type FileRejection } from 'react-dropzone';
import styles from './FileDropzone.module.css';

/** Maximum file size in bytes (50MB per FR-006) */
const MAX_FILE_SIZE = 50 * 1024 * 1024;

/** Accepted file extensions */
const ACCEPTED_EXTENSIONS = ['.cube'];

export interface FileDropzoneProps {
  /** Accept multiple files (for bulk mode) */
  multiple?: boolean;
  /** Maximum file size in bytes (default: 50MB) */
  maxSize?: number;
  /** Callback when valid files are added */
  onFilesAdded: (files: File[]) => void;
  /** Callback for validation errors */
  onError?: (error: string) => void;
  /** Disabled state during conversion */
  disabled?: boolean;
  /** Currently selected file (for single mode) */
  selectedFile?: File | null;
}

export function FileDropzone({
  multiple = false,
  maxSize = MAX_FILE_SIZE,
  onFilesAdded,
  onError,
  disabled = false,
  selectedFile,
}: FileDropzoneProps) {
  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
      if (rejectedFiles.length > 0) {
        const errors = rejectedFiles.map((rejection) => {
          const errorMessages = rejection.errors.map((e) => e.message).join(', ');
          return `${rejection.file.name}: ${errorMessages}`;
        });
        onError?.(errors.join('\n'));
        return;
      }

      const validFiles: File[] = [];
      const invalidFiles: string[] = [];

      for (const file of acceptedFiles) {
        const ext = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
        if (!ACCEPTED_EXTENSIONS.includes(ext)) {
          invalidFiles.push(`${file.name}: Only .cube files are accepted`);
        } else if (file.size > maxSize) {
          invalidFiles.push(`${file.name}: File exceeds 50MB limit`);
        } else {
          validFiles.push(file);
        }
      }

      if (invalidFiles.length > 0) onError?.(invalidFiles.join('\n'));
      if (validFiles.length > 0) onFilesAdded(validFiles);
    },
    [maxSize, onFilesAdded, onError]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    disabled,
    accept: {
      'application/octet-stream': ACCEPTED_EXTENSIONS,
      'text/plain': ACCEPTED_EXTENSIONS,
    },
    maxSize,
  });

  const rootClass = useMemo(
    () =>
      [
        styles.dropzone,
        isDragActive && styles.active,
        disabled && styles.disabled,
        selectedFile && styles.hasFile,
      ]
        .filter(Boolean)
        .join(' '),
    [isDragActive, disabled, selectedFile]
  );

  return (
    <div
      {...getRootProps({
        role: 'button',
        tabIndex: 0,
        'aria-disabled': disabled,
        'aria-label': multiple ? 'Add .cube files' : 'Add a .cube file',
      })}
      className={rootClass}
    >
      <input {...getInputProps()} />

      {selectedFile ? (
        <>
          <div className={styles.iconWrap}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
              <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
              <polyline points="14,2 14,8 20,8" />
              <line x1="9" y1="13" x2="15" y2="13" />
              <line x1="9" y1="17" x2="13" y2="17" />
            </svg>
          </div>
          <p className={styles.primary}>{selectedFile.name}</p>
          <p className={styles.secondary}>{formatFileSize(selectedFile.size)} · Drop a different file to replace</p>
        </>
      ) : (
        <>
          <div className={styles.iconWrap}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" width="22" height="22">
              <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" />
              <polyline points="17 8 12 3 7 8" />
              <line x1="12" y1="3" x2="12" y2="15" />
            </svg>
          </div>
          <p className={styles.primary}>
            {isDragActive
              ? 'Drop the file here'
              : multiple
              ? 'Drop your .cube files here'
              : 'Drop your .cube file here'}
          </p>
          <p className={styles.secondary}>
            or <span className={styles.browse}>browse to select</span>
          </p>
          <div className={styles.badges}>
            <span className={styles.badge}>.cube</span>
            <span className={styles.badge}>1D / 3D</span>
            <span className={styles.badge}>up to {formatFileSize(maxSize)}</span>
          </div>
        </>
      )}
    </div>
  );
}

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default FileDropzone;
